import { useCallback, useEffect, useMemo, useState } from "react";

import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { crmContractService } from "../../contracts/services/crmContractService";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { endpointService } from "../../messaging/services/endpointService";

export interface UseCrmSetupEligibilityResult {
  /** True quando deve exibir o stepper (nenhum endpoint/contrato e onboarding não finalizado em sessão). */
  shouldShowStepper: boolean;
  /** Endpoint ativo existente (Meta). */
  hasActiveMetaEndpoint: boolean;
  /** Contrato CRM existente (Fluxmed). */
  hasCrmContract: boolean;
  /** Extensão FluxZap ativa (contrato `pos-atendimento` executado). */
  hasFluxZapExtensionActive: boolean;
  /** Loading da avaliação real (endpoint/contrato). */
  isLoading: boolean;
  /** Quando usuário finaliza manualmente na sessão (Fluxmed por enquanto). */
  markOnboardingCompletedInSession: () => void;
  /** Recarrega endpoint/contrato após assinatura ou alteração de plano. */
  refetch: () => void;
}

export interface UseCrmSetupEligibilityProps {
  organizationId?: string;
}

export function useCrmSetupEligibility(
  props: UseCrmSetupEligibilityProps = {},
): UseCrmSetupEligibilityResult {
  const organizationId = props.organizationId;

  /**
   * A extensão FluxZap corresponde ao contrato `pos-atendimento`, já carregado
   * globalmente pelo `PostAttendanceContext`. Reaproveitamos para evitar uma
   * busca FHIR duplicada.
   */
  const { contractPostAttendance, refetchContract } =
    usePostAttendanceContext();
  const hasFluxZapExtensionActive = Boolean(contractPostAttendance?.id);

  const [hasActiveMetaEndpoint, setHasActiveMetaEndpoint] = useState(false);
  const [hasCrmContract, setHasCrmContract] = useState(false);
  const [isOnboardingCompletedInSession, setIsOnboardingCompletedInSession] =
    useState(false);

  const { run: runLoad, loading: isLoading } = useAsyncTask<
    [],
    {
      hasActiveMetaEndpoint: boolean;
      hasCrmContract: boolean;
    }
  >({
    task: useCallback(async () => {
      const [endpoint, crmContract] = await Promise.all([
        endpointService.getActiveWhatsAppEndpointByContext({ organizationId }),
        crmContractService.hasActiveCrmContractByOrganization({
          organizationId,
        }),
      ]);

      const next = {
        hasActiveMetaEndpoint: Boolean(
          endpoint?.id && endpoint.status === "active",
        ),
        hasCrmContract: Boolean(crmContract),
      };

      setHasActiveMetaEndpoint(next.hasActiveMetaEndpoint);
      setHasCrmContract(next.hasCrmContract);
      return next;
    }, [organizationId]),
  });

  useEffect(() => {
    runLoad();
  }, [runLoad]);

  const refetch = useCallback(() => {
    void runLoad();
    void refetchContract();
  }, [runLoad, refetchContract]);

  /**
   * Exibe o stepper de primeiro acesso enquanto nenhum canal estiver configurado
   * (sem endpoint Meta ativo e sem contrato Fluxmed) e o usuário ainda não tiver
   * concluído o wizard na sessão atual.
   */
  const shouldShowStepper = useMemo(
    () =>
      !isOnboardingCompletedInSession &&
      !hasActiveMetaEndpoint &&
      !hasCrmContract,
    [isOnboardingCompletedInSession, hasActiveMetaEndpoint, hasCrmContract],
  );

  const markOnboardingCompletedInSession = useCallback(() => {
    setIsOnboardingCompletedInSession(true);
  }, []);

  return {
    shouldShowStepper,
    hasActiveMetaEndpoint,
    hasCrmContract,
    hasFluxZapExtensionActive,
    isLoading,
    markOnboardingCompletedInSession,
    refetch,
  };
};
